import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import useUsuarios from "../hooks/useUsuarios";

const FORM_VACIO = { id: null, usuario: "", password: "", nombreCajero: "", idPlantel: "", idRol: "" };

const VistaUsuarios = () => {
  const { usuarios, roles, planteles, cargando, guardando, crearUsuario, actualizarUsuario, cambiarPassword, cambiarEstatus } = useUsuarios();
  const [mostrarForm, setMostrarForm] = useState(false);
  const [modoForm, setModoForm] = useState("crear"); // 'crear' | 'editar'
  const [form, setForm] = useState(FORM_VACIO);
  const [busqueda, setBusqueda] = useState("");
  const formRef = useRef(null);

  // Al abrir el formulario (crear o editar), lo llevamos a la vista: si la
  // lista tiene scroll, el cajero podia no notar que el formulario aparecio
  // hasta arriba de la pantalla.
  useEffect(() => {
    if (mostrarForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [mostrarForm]);

  const usuariosFiltrados = busqueda.trim()
    ? usuarios.filter((u) => u.nombre_cajero.toLowerCase().includes(busqueda.trim().toLowerCase()))
    : usuarios;

  const abrirCrear = () => {
    setForm(FORM_VACIO);
    setModoForm("crear");
    setMostrarForm(true);
  };

  const abrirEditar = (u) => {
    setForm({ id: u.id, usuario: u.usuario, password: "", nombreCajero: u.nombre_cajero, idPlantel: u.id_plantel, idRol: u.id_rol });
    setModoForm("editar");
    setMostrarForm(true);
  };

  const cerrarForm = () => {
    setMostrarForm(false);
    setForm(FORM_VACIO);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nombreCajero.trim() || !form.idPlantel || !form.idRol) {
      Swal.fire("Atención", "Nombre, plantel y rol son obligatorios.", "warning");
      return;
    }

    let ok;
    if (modoForm === "crear") {
      if (!form.usuario.trim() || !form.password) {
        Swal.fire("Atención", "Usuario y contraseña son obligatorios para crear un cajero.", "warning");
        return;
      }
      ok = await crearUsuario({
        usuario: form.usuario.trim(),
        password: form.password,
        nombreCajero: form.nombreCajero.trim(),
        idPlantel: form.idPlantel,
        idRol: form.idRol
      });
    } else {
      ok = await actualizarUsuario({
        id: form.id,
        nombreCajero: form.nombreCajero.trim(),
        idPlantel: form.idPlantel,
        idRol: form.idRol
      });
    }

    if (ok) cerrarForm();
  };

  const handleResetPassword = async (u) => {
    const { value: passwordNuevo } = await Swal.fire({
      title: `Nueva contraseña para ${u.nombre_cajero}`,
      input: "password",
      inputLabel: "Mínimo 8 caracteres",
      inputPlaceholder: "Contraseña nueva",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar"
    });

    if (passwordNuevo) {
      await cambiarPassword(u.id, passwordNuevo);
    }
  };

  const handleToggleEstatus = async (u) => {
    const activar = parseInt(u.estatus, 10) !== 1;
    const confirmacion = await Swal.fire({
      title: activar ? "¿Activar este cajero?" : "¿Desactivar este cajero?",
      text: activar
        ? `${u.nombre_cajero} podrá iniciar sesión de nuevo.`
        : `${u.nombre_cajero} ya no podrá iniciar sesión, pero su historial de cobros se conserva.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: activar ? "Sí, activar" : "Sí, desactivar",
      cancelButtonText: "Cancelar"
    });

    if (confirmacion.isConfirmed) {
      await cambiarEstatus(u.id, activar ? 1 : 0);
    }
  };

  return (
    <div className="Dashboard-card" style={{ marginTop: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ margin: 0, color: "#003b5c" }}>Cajeros</h3>
        <button
          className="btn-select validar"
          style={{ border: "none", padding: "10px 18px", cursor: "pointer" }}
          onClick={abrirCrear}
        >
          + Nuevo cajero
        </button>
      </div>

      {mostrarForm && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px",
            padding: "18px", backgroundColor: "#f8f9fa", borderRadius: "8px", marginBottom: "20px",
            // El header de arriba es fijo (position: fixed, 75px) y tapa lo que
            // scrollIntoView() posiciona justo en el borde superior; este margen
            // le dice al navegador que deje ese espacio libre al hacer scroll aqui.
            scrollMarginTop: "95px"
          }}
        >
          <div>
            <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem", marginBottom: "4px" }}>Usuario (correo)</label>
            <input
              name="usuario" type="text" value={form.usuario} onChange={handleChange}
              disabled={modoForm === "editar"}
              placeholder="nombre.apellido@cecytejalisco.edu.mx"
              style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
            />
          </div>

          {modoForm === "crear" && (
            <div>
              <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem", marginBottom: "4px" }}>Contraseña</label>
              <input
                name="password" type="password" value={form.password} onChange={handleChange}
                placeholder="Mínimo 8 caracteres"
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
            </div>
          )}

          <div>
            <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem", marginBottom: "4px" }}>Nombre completo</label>
            <input
              name="nombreCajero" type="text" value={form.nombreCajero} onChange={handleChange}
              style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem", marginBottom: "4px" }}>Plantel</label>
            <select
              name="idPlantel" value={form.idPlantel} onChange={handleChange}
              style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
            >
              <option value="">Selecciona...</option>
              {planteles.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre_plantel}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem", marginBottom: "4px" }}>Rol</label>
            <select
              name="idRol" value={form.idRol} onChange={handleChange}
              style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
            >
              <option value="">Selecciona...</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.nombre_rol}{r.descripcion ? ` — ${r.descripcion}` : ""}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
            <button
              type="submit" disabled={guardando}
              className="btn-select validar"
              style={{ border: "none", padding: "10px 18px", cursor: "pointer" }}
            >
              {guardando ? "Guardando..." : modoForm === "crear" ? "Crear cajero" : "Guardar cambios"}
            </button>
            <button
              type="button" onClick={cerrarForm}
              className="btn-logout"
              style={{ padding: "10px 18px" }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {!cargando && (
        <div style={{ marginBottom: "15px" }}>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar cajero por nombre..."
            style={{ width: "100%", maxWidth: "320px", padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px" }}
          />
        </div>
      )}

      {cargando ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
          <p><strong>Cargando cajeros...</strong></p>
        </div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead style={{ backgroundColor: "#f4f4f4", borderBottom: "2px solid #ddd" }}>
              <tr>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem" }}>Nombre</th>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem" }}>Usuario</th>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem" }}>Plantel</th>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem" }}>Rol</th>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem", textAlign: "center" }}>Estatus</th>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem", textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.map((u) => (
                <tr key={u.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "12px 15px", fontSize: "0.9rem" }}>{u.nombre_cajero}</td>
                  <td style={{ padding: "12px 15px", fontSize: "0.85rem", color: "#555" }}>{u.usuario}</td>
                  <td style={{ padding: "12px 15px", fontSize: "0.9rem" }}>{u.nombre_plantel}</td>
                  <td style={{ padding: "12px 15px", fontSize: "0.9rem" }}>{u.nombre_rol}</td>
                  <td style={{ padding: "12px 15px", textAlign: "center" }}>
                    <span style={{
                      backgroundColor: parseInt(u.estatus, 10) === 1 ? "#d4edda" : "#f8d7da",
                      color: parseInt(u.estatus, 10) === 1 ? "#155724" : "#721c24",
                      padding: "4px 10px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "bold"
                    }}>
                      {parseInt(u.estatus, 10) === 1 ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 15px", textAlign: "center", whiteSpace: "nowrap" }}>
                    <button onClick={() => abrirEditar(u)} title="Editar" style={{ border: "none", background: "none", cursor: "pointer", fontSize: "1.1rem", marginRight: "8px" }}>✏️</button>
                    <button onClick={() => handleResetPassword(u)} title="Restablecer contraseña" style={{ border: "none", background: "none", cursor: "pointer", fontSize: "1.1rem", marginRight: "8px" }}>🔑</button>
                    <button onClick={() => handleToggleEstatus(u)} title={parseInt(u.estatus, 10) === 1 ? "Desactivar" : "Activar"} style={{ border: "none", background: "none", cursor: "pointer", fontSize: "1.1rem" }}>
                      {parseInt(u.estatus, 10) === 1 ? "🚫" : "✅"}
                    </button>
                  </td>
                </tr>
              ))}
              {usuariosFiltrados.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: "30px", textAlign: "center", color: "#888" }}>
                    {usuarios.length === 0 ? "No hay cajeros registrados." : "Ningún cajero coincide con la búsqueda."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VistaUsuarios;
